import React, { useCallback, useEffect, useState } from 'react'
import { Button, ButtonGroup } from 'react-bootstrap'
import Alert from 'react-bootstrap/Alert'
import Pagination from 'react-bootstrap/Pagination'
import Helmet from 'react-helmet'
import { useHistory } from 'react-router-dom'

import { useDebouncedCallback } from 'use-debounce'

import { SkeletonRow } from '../components/common/SkeletonRow'
import SmartLibraryReviewModal from '../components/library/SmartLibraryReviewModal'
import ProviderForm from '../components/ProviderForm'
import ProviderService from '../services/ProviderService'
import { paginate } from '../utils/paginate'

export default function ProvidersPage({ type }) {
  const history = useHistory()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [providers, setProviders] = useState({ results: [], total: 0, page: 1, totalPages: 1 })

  // Modal State
  const [showModal, setShowModal] = useState(false)
  const [selectedProviderId, setSelectedProviderId] = useState(null)
  const [createClassification, setCreateClassification] = useState(
    type === 'Pharmacy' || type === 'Equipment' ? 'Organization' : 'Individual',
  )
  const [showSmartReview, setShowSmartReview] = useState(false)

  // Search State
  const [searchInput, setSearchInput] = useState({
    page: 1,
    perPage: 20,
    searchText: '',
    type: type || '',
    classification: '',
  })
  const [searchTextVal, setSearchTextVal] = useState('')

  const fetchProviders = useCallback(() => {
    setIsLoading(true)
    ProviderService.search(searchInput)
      .then(data => {
        setProviders(data)
        setError('')
      })
      .catch(err => {
        setError('Failed to load providers')
        // eslint-disable-next-line no-console
        console.warn(err)
      })
      .finally(() => setIsLoading(false))
  }, [searchInput])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const id = params.get('id')
    const search = params.get('search')
    if (id) {
      setSelectedProviderId(id)
      setShowModal(true)
    }
    if (search) {
      setSearchTextVal(search)
      setSearchInput(prev => ({ ...prev, searchText: search, page: 1 }))
    }
  }, [type])

  useEffect(() => {
    fetchProviders()
  }, [fetchProviders])

  const debouncedSetSearch = useDebouncedCallback(
    text => setSearchInput(prev => ({ ...prev, searchText: text, page: 1 })),
    500,
  )

  const handleSearchTextChange = e => {
    const val = e.target.value
    setSearchTextVal(val)
    debouncedSetSearch(val)
  }

  const handleClassificationFilterChange = classification => {
    setSearchInput(prev => ({ ...prev, classification, page: 1 }))
  }

  const setPage = page => {
    return () => setSearchInput(prev => ({ ...prev, page }))
  }

  const handleAdd = classification => {
    setSelectedProviderId(null)
    setCreateClassification(
      classification || (type === 'Pharmacy' || type === 'Equipment' ? 'Organization' : 'Individual'),
    )
    setShowModal(true)
  }

  const handleEdit = id => {
    setSelectedProviderId(id)
    setShowModal(true)
  }

  const handleModalHide = () => {
    setShowModal(false)
    setSelectedProviderId(null)
    const params = new URLSearchParams(window.location.search)
    if (params.get('id')) {
      history.goBack()
    }
  }

  const handleSaveSuccess = () => {
    fetchProviders()
    const params = new URLSearchParams(window.location.search)
    if (params.get('id')) {
      history.goBack()
    }
  }

  // Pagination Logic similar to Cases.js
  const pagingItems =
    providers.total > 0
      ? paginate(providers.page, providers.totalPages).map((number, index) => {
          if (number === '…') {
            return <Pagination.Ellipsis key={number + index} disabled />
          }
          return (
            <Pagination.Item key={number} active={number === providers.page} onClick={setPage(number)}>
              {number}
            </Pagination.Item>
          )
        })
      : []

  return (
    <div className="providers-page-container pb-3">
      <Helmet title={type ? `${type} Providers` : 'Providers'} />
      <div className="d-flex justify-content-between align-items-center flex-wrap my-3">
        <h4 className="mb-0 text-dark font-weight-bold">{type ? `${type} Providers` : 'Providers'}</h4>
        <div className="d-flex align-items-center flex-wrap mt-2 mt-sm-0">
          <button className="btn btn-outline-primary mr-2" onClick={() => setShowSmartReview(true)}>
            ✨ AI Smart Library Review
          </button>
          <button className="btn btn-primary mr-2" onClick={() => handleAdd('Individual')}>
            <span role="img" aria-label="individual" className="mr-1">
              👤
            </span>
            + New Individual
          </button>
          <button className="btn btn-primary" onClick={() => handleAdd('Organization')}>
            <span role="img" aria-label="organization" className="mr-1">
              🏢
            </span>
            + New Organization
          </button>
        </div>
      </div>

      {error && (
        <Alert variant="danger">
          <strong>Error:</strong> {error}
        </Alert>
      )}

      <div className="d-flex flex-row my-2 align-items-center flex-wrap">
        <div className="flex-grow-1 mr-sm-3 mb-2 mb-sm-0">
          <input
            type="text"
            className="form-control"
            placeholder={
              type === 'Pharmacy'
                ? 'Search pharmacies...'
                : searchInput.classification === 'Individual'
                ? 'Search individual providers...'
                : searchInput.classification === 'Organization'
                ? 'Search organizations...'
                : 'Search providers...'
            }
            value={searchTextVal}
            onChange={handleSearchTextChange}
          />
        </div>
        <ButtonGroup aria-label="Filter by Entity Type">
          <Button
            variant={!searchInput.classification ? 'primary' : 'outline-secondary'}
            onClick={() => handleClassificationFilterChange('')}
          >
            All
          </Button>
          <Button
            variant={searchInput.classification === 'Individual' ? 'primary' : 'outline-secondary'}
            onClick={() => handleClassificationFilterChange('Individual')}
          >
            <span role="img" aria-label="individual" className="mr-1">
              👤
            </span>
            Individuals
          </Button>
          <Button
            variant={searchInput.classification === 'Organization' ? 'primary' : 'outline-secondary'}
            onClick={() => handleClassificationFilterChange('Organization')}
          >
            <span role="img" aria-label="organization" className="mr-1">
              🏢
            </span>
            Organizations
          </Button>
        </ButtonGroup>
      </div>

      <div className="card border-light">
        <table className="table mb-0">
          <thead>
            <tr>
              <th>Individual</th>
              <th>Organization</th>
              <th>Address</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : providers.results.length > 0 ? (
              providers.results.map(p => (
                <tr key={p.id + (p.name || 'org')} onClick={() => handleEdit(p.id)} style={{ cursor: 'pointer' }}>
                  <td>{p.name}</td>
                  <td>{p.organization}</td>
                  <td>{p.address}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="text-center">
                  No providers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {providers.totalPages > 1 && (
        <div className="d-flex justify-content-center">
          <Pagination>{pagingItems}</Pagination>
        </div>
      )}

      <ProviderForm
        show={showModal}
        onHide={handleModalHide}
        providerId={selectedProviderId}
        onSuccess={handleSaveSuccess}
        initialType={searchInput.type}
        initialClassification={createClassification}
      />

      <SmartLibraryReviewModal
        show={showSmartReview}
        onHide={() => setShowSmartReview(false)}
        providerType={searchInput.type}
        onComplete={fetchProviders}
      />
    </div>
  )
}
